import { UnitRecord } from './equipmentTypes';

/** Fields present in UI + localStorage until deployed AppSync SingleUnit schema matches amplify/data. */
export type UnitLocalFieldSnapshot = Pick<
    UnitRecord,
    | 'assignedTo'
    | 'engineManufacturer'
    | 'engineModelNumber'
    | 'engineSerialNumber'
    | 'utilizationAdjustmentDays'
    | 'editHistory'
    | 'replacedUMCounterHistory'
>;

const STORAGE_KEY = 'eif-single-unit-local-fields';
const LEGACY_ENGINE_STORAGE_KEY = 'eif-single-unit-engine-fields';

type LocalFieldStore = Record<string, UnitLocalFieldSnapshot>;

const readStore = (key: string): LocalFieldStore => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as LocalFieldStore;
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const readLegacyEngineEntry = (unitId: string): UnitLocalFieldSnapshot => {
    const legacy = readStore(LEGACY_ENGINE_STORAGE_KEY)[unitId];
    if (!legacy) return {};
    return {
        engineManufacturer: legacy.engineManufacturer ?? null,
        engineModelNumber: legacy.engineModelNumber ?? null,
    };
};

const coerceAdjustmentDays = (value: unknown): number | null => {
    if (value == null || value === '') return null;
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, n);
};

const coerceEditHistory = (value: unknown): string | null => {
    if (value == null || value === '') return null;
    return typeof value === 'string' ? value : null;
};

const coerceHistoryJson = coerceEditHistory;

export function readUnitLocalFields(unitId: string): UnitLocalFieldSnapshot {
    const current = readStore(STORAGE_KEY)[unitId] ?? {};
    const legacy = readLegacyEngineEntry(unitId);
    return {
        assignedTo: current.assignedTo ?? legacy.assignedTo ?? null,
        engineManufacturer: current.engineManufacturer ?? legacy.engineManufacturer ?? null,
        engineModelNumber: current.engineModelNumber ?? legacy.engineModelNumber ?? null,
        engineSerialNumber: current.engineSerialNumber ?? legacy.engineSerialNumber ?? null,
        utilizationAdjustmentDays: coerceAdjustmentDays(current.utilizationAdjustmentDays),
        editHistory: coerceEditHistory(current.editHistory),
        replacedUMCounterHistory: coerceHistoryJson(current.replacedUMCounterHistory),
    };
}

export function writeUnitLocalFields(unitId: string, fields: UnitLocalFieldSnapshot): void {
    const store = readStore(STORAGE_KEY);
    const nextEntry: UnitLocalFieldSnapshot = {
        assignedTo: fields.assignedTo?.trim() || null,
        engineManufacturer: fields.engineManufacturer?.trim() || null,
        engineModelNumber: fields.engineModelNumber?.trim() || null,
        engineSerialNumber: fields.engineSerialNumber?.trim() || null,
        utilizationAdjustmentDays: coerceAdjustmentDays(fields.utilizationAdjustmentDays),
        editHistory: coerceEditHistory(fields.editHistory),
        replacedUMCounterHistory: coerceHistoryJson(fields.replacedUMCounterHistory),
    };

    const hasAny = Boolean(
        nextEntry.assignedTo ||
            nextEntry.engineManufacturer ||
            nextEntry.engineModelNumber ||
            nextEntry.engineSerialNumber ||
            nextEntry.utilizationAdjustmentDays != null ||
            nextEntry.editHistory ||
            nextEntry.replacedUMCounterHistory,
    );
    if (!hasAny) {
        delete store[unitId];
    } else {
        store[unitId] = nextEntry;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

export function mergeUnitLocalFields<T extends UnitRecord>(unitId: string, unit: T): T {
    const stored = readUnitLocalFields(unitId);
    return {
        ...unit,
        ...(stored.assignedTo != null ? { assignedTo: stored.assignedTo } : {}),
        ...(stored.engineManufacturer != null ? { engineManufacturer: stored.engineManufacturer } : {}),
        ...(stored.engineModelNumber != null ? { engineModelNumber: stored.engineModelNumber } : {}),
        ...(stored.engineSerialNumber != null ? { engineSerialNumber: stored.engineSerialNumber } : {}),
        ...(stored.utilizationAdjustmentDays != null
            ? { utilizationAdjustmentDays: stored.utilizationAdjustmentDays }
            : {}),
        ...(stored.editHistory != null ? { editHistory: stored.editHistory } : {}),
        ...(stored.replacedUMCounterHistory != null
            ? { replacedUMCounterHistory: stored.replacedUMCounterHistory }
            : {}),
    };
}

/** @deprecated Use readUnitLocalFields */
export function readUnitEngineFields(unitId: string) {
    const { engineManufacturer, engineModelNumber } = readUnitLocalFields(unitId);
    return { engineManufacturer, engineModelNumber };
}

/** @deprecated Use writeUnitLocalFields */
export function writeUnitEngineFields(
    unitId: string,
    fields: Pick<UnitRecord, 'engineManufacturer' | 'engineModelNumber'>,
) {
    const existing = readUnitLocalFields(unitId);
    writeUnitLocalFields(unitId, { ...existing, ...fields });
}

/** @deprecated Use mergeUnitLocalFields */
export function mergeUnitEngineFields<T extends UnitRecord>(unitId: string, unit: T): T {
    return mergeUnitLocalFields(unitId, unit);
}
